(() => {
  const config = window.YB_AUTO_LOCALIZATION || {};
  const endpoint = config.endpoint || '/browsing_context_suggestions.json';
  const localizationPath = config.localizationPath || '/localization';
  const currentCountry = String(config.currentCountry || '').toUpperCase();
  const currentLanguage = String(config.currentLanguage || '').toLowerCase();
  const currentCurrency = String(config.currentCurrency || '').toUpperCase();
  const availableCountries = new Set(
    (config.availableCountries || []).map((countryCode) => String(countryCode).toUpperCase()).filter(Boolean)
  );
  const availableLanguages = new Map(
    (config.availableLanguages || [])
      .map((languageCode) => String(languageCode))
      .filter(Boolean)
      .map((languageCode) => [languageCode.toLowerCase(), languageCode])
  );
  const configDefaultCountry = String(config.defaultCountry || 'US').toUpperCase();
  const supportedDefaultCountry = availableCountries.has(configDefaultCountry) ? configDefaultCountry : 'US';
  const defaultCountry = supportedDefaultCountry;
  const defaultLanguage = String(config.defaultLanguage || 'en');
  const countryLanguageMap = {
    CN: 'zh-CN',
  };
  const countryCurrencyMap = Object.entries(config.countryCurrencyMap || {}).reduce((map, [countryCode, currencyCode]) => {
    map[String(countryCode).toUpperCase()] = String(currencyCode).toUpperCase();
    return map;
  }, {});

  const manualChoiceKey = 'yb-localization-manual-choice';
  const manualCountryKey = 'yb-localization-country';
  const manualLanguageKey = 'yb-localization-language';
  const manualAttemptedKey = 'yb-manual-localization-attempted';
  const attemptedKey = 'yb-auto-localization-attempted';

  const getCookieValue = (key) => {
    const cookies = document.cookie ? document.cookie.split(';') : [];
    const prefix = `${key}=`;
    const cookie = cookies.map((item) => item.trim()).find((item) => item.startsWith(prefix));
    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
  };

  const hasManualChoice = () =>
    getStorageValue('localStorage', manualChoiceKey) === 'true' || getCookieValue(manualChoiceKey) === 'true';

  const getStorageValue = (storageName, key) => {
    try {
      return window[storageName].getItem(key);
    } catch (error) {
      return null;
    }
  };

  const setStorageValue = (storageName, key, value) => {
    try {
      window[storageName].setItem(key, value);
    } catch (error) {
      // Storage can be blocked in strict privacy modes. The page should still work.
    }
  };

  const getSuggestedCountry = (payload) => {
    const detectedCountry = payload?.detected_values?.country?.handle;
    if (detectedCountry) return String(detectedCountry).toUpperCase();

    const suggestions = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
    for (const suggestion of suggestions) {
      const countryCode =
        suggestion?.country?.handle ||
        suggestion?.country?.iso_code ||
        suggestion?.country_code ||
        suggestion?.iso_code;

      if (countryCode) return String(countryCode).toUpperCase();
    }

    return '';
  };

  const getSupportedTargetCountry = (countryCode) => {
    const normalizedCountry = String(countryCode || '').toUpperCase();
    if (normalizedCountry && availableCountries.has(normalizedCountry)) {
      return normalizedCountry;
    }

    return supportedDefaultCountry;
  };

  const getAvailableLanguage = (preferredLanguage = defaultLanguage) => {
    if (!availableLanguages.size) return '';
    const normalizedPreferredLanguage = preferredLanguage.toLowerCase();

    if (availableLanguages.has(normalizedPreferredLanguage)) {
      return availableLanguages.get(normalizedPreferredLanguage);
    }

    if (normalizedPreferredLanguage.includes('-')) {
      const baseLanguage = normalizedPreferredLanguage.split('-')[0];
      if (availableLanguages.has(baseLanguage)) {
        return availableLanguages.get(baseLanguage);
      }
    }

    return availableLanguages.get(defaultLanguage.toLowerCase()) || '';
  };

  const getLanguageForCountry = (countryCode) => getAvailableLanguage(countryLanguageMap[countryCode] || defaultLanguage);

  const getManualPreference = () => {
    if (!hasManualChoice()) return null;

    const savedCountry = getStorageValue('localStorage', manualCountryKey) || getCookieValue(manualCountryKey);
    const countryCode = getSupportedTargetCountry(savedCountry);
    const languageCode = getLanguageForCountry(countryCode);

    return {
      countryCode,
      languageCode,
    };
  };

  const appendHiddenField = (form, name, value) => {
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.value = value;
    form.appendChild(field);
  };

  const submitLocalizationChange = ({ countryCode, languageCode }) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = localizationPath;
    form.style.display = 'none';

    appendHiddenField(form, 'form_type', 'localization');
    appendHiddenField(form, 'utf8', '✓');
    appendHiddenField(form, '_method', 'put');
    if (countryCode) appendHiddenField(form, 'country_code', countryCode);
    if (languageCode) appendHiddenField(form, 'language_code', languageCode);
    appendHiddenField(form, 'return_to', `${window.location.pathname}${window.location.search}${window.location.hash}`);

    document.body.appendChild(form);
    form.submit();
  };

  const applyManualPreference = () => {
    const manualPreference = getManualPreference();
    if (!manualPreference) return false;

    const shouldChangeCountry = manualPreference.countryCode && manualPreference.countryCode !== currentCountry;
    const shouldChangeLanguage =
      manualPreference.languageCode && manualPreference.languageCode.toLowerCase() !== currentLanguage;

    if (!shouldChangeCountry && !shouldChangeLanguage) {
      return true;
    }

    const attemptedValue = `${manualPreference.countryCode}:${manualPreference.languageCode || ''}`;
    if (getStorageValue('sessionStorage', manualAttemptedKey) === attemptedValue) {
      return true;
    }

    setStorageValue('sessionStorage', manualAttemptedKey, attemptedValue);
    submitLocalizationChange({
      countryCode: shouldChangeCountry ? manualPreference.countryCode : '',
      languageCode: shouldChangeLanguage ? manualPreference.languageCode : '',
    });

    return true;
  };

  const runAutoLocalization = async () => {
    if (window.Shopify?.designMode) return;
    if (!currentCountry || (availableCountries.size <= 1 && availableLanguages.size <= 1)) return;
    if (window.location.pathname.includes('/challenge')) return;
    if (applyManualPreference()) return;

    const currentCountryIsSupported = availableCountries.has(currentCountry);
    const expectedCurrency = countryCurrencyMap[currentCountry];
    const expectedLanguage = getLanguageForCountry(currentCountry);
    const currentCurrencyMismatched = Boolean(expectedCurrency && currentCurrency && currentCurrency !== expectedCurrency);
    const currentLanguageMismatched = Boolean(
      expectedLanguage && currentLanguage && expectedLanguage.toLowerCase() !== currentLanguage
    );
    if (
      getStorageValue('sessionStorage', attemptedKey) === 'true' &&
      currentCountryIsSupported &&
      !currentCurrencyMismatched &&
      !currentLanguageMismatched
    )
      return;

    setStorageValue('sessionStorage', attemptedKey, 'true');

    let targetCountry = supportedDefaultCountry;

    if (currentCountryIsSupported && (currentCurrencyMismatched || currentLanguageMismatched)) {
      targetCountry = currentCountry;
    } else {
      try {
        const response = await fetch(endpoint, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        if (!response.ok) throw new Error('Browsing context suggestions failed.');

        const payload = await response.json();
        const suggestedCountry = getSuggestedCountry(payload);
        targetCountry = getSupportedTargetCountry(suggestedCountry);
      } catch (error) {
        targetCountry = supportedDefaultCountry;
      }
    }

    const targetLanguage = getLanguageForCountry(targetCountry);
    const shouldChangeCountry = targetCountry && targetCountry !== currentCountry;
    const shouldChangeLanguage = targetLanguage && targetLanguage.toLowerCase() !== currentLanguage;

    if (!shouldChangeCountry && !shouldChangeLanguage) {
      return;
    }

    submitLocalizationChange({
      countryCode: shouldChangeCountry ? targetCountry : '',
      languageCode: shouldChangeLanguage ? targetLanguage : '',
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAutoLocalization, { once: true });
  } else {
    runAutoLocalization();
  }
})();
